import React, { useState, useMemo } from 'react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, ReferenceLine,
  Legend
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Users, Activity, Zap } from 'lucide-react';
import { DailyMetalData, MetalChartData } from '../types/metal';
import { formatNumberDisplay } from '@/lib/utils';
import { getMetalChartData } from '../integrations/sqlserver/metalService';

interface MetalChartsProps {
  dailyData: DailyMetalData[];
  isLoading: boolean;
  error: string | null;
  processes: string[];
  title: string;
}

const MetalCharts: React.FC<MetalChartsProps> = ({ 
  dailyData, 
  isLoading, 
  error, 
  processes, 
  title 
}) => {
  const [activeMetric, setActiveMetric] = useState<'labor' | 'weight' | 'productivity'>('weight');

  // Process names mapping
  const processNames = {
    phoi: 'Phôi',
    han: 'Hàn',
    son: 'Sơn',
    kiemTran: 'Kiểm trần',
    kiemDan: 'Kiểm đan',
    thanhPham: 'Thành phẩm',
  };

  // Process colors
  const processColors = {
    phoi: '#f59e0b',      // Amber
    han: '#ef4444',       // Red
    son: '#3b82f6',       // Blue
    kiemTran: '#8b5cf6',  // Violet
    kiemDan: '#10b981',   // Emerald
    thanhPham: '#f97316', // Orange
  };

  // Filter chart data based on selected processes
  const chartData = useMemo(() => {
    if (!dailyData.length) return [];
    const data = getMetalChartData(dailyData, activeMetric);
    return data;
  }, [dailyData, activeMetric]);

  // Calculate average values for trendlines
  const averageValues = useMemo(() => {
    if (!chartData.length) return {};
    
    // Calculate working days (days where at least one relevant process has data > 0)
    const workingDays = chartData.filter(day => {
      return processes.some(processKey => {
        const value = day[processKey as keyof MetalChartData];
        return typeof value === 'number' && value > 0;
      });
    });

    const averages: Record<string, number> = {};
    
    processes.forEach(processKey => {
      if (workingDays.length > 0) {
        const sum = workingDays.reduce((acc, day) => {
          const value = day[processKey as keyof MetalChartData];
          return acc + (typeof value === 'number' ? value : 0);
        }, 0);
        averages[processKey] = sum / workingDays.length;
      } else {
        averages[processKey] = 0;
      }
    });

    return averages;
  }, [chartData, processes]);

  // Get unit for metrics
  const getUnit = () => {
    switch (activeMetric) {
      case 'labor': return 'Công';
      case 'weight': return 'Kg';
      case 'productivity': return 'Kg/Công';
      default: return '';
    }
  };

  // Get title for metrics
  const getTitle = () => {
    switch (activeMetric) {
      case 'labor': return 'Công lao động';
      case 'weight': return 'Kg Công suất';
      case 'productivity': return 'Năng suất';
      default: return '';
    }
  };

  // Format numbers
  // Use Vietnamese locale formatting for charts

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-red-600">Lỗi tải biểu đồ: {error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            {title}
          </div>
          
          {/* Metric Selection Buttons - Top Right */}
          <div className="flex flex-wrap items-center gap-2 justify-end md:justify-start">
            <Button
              variant={activeMetric === 'labor' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveMetric('labor')}
              className="flex items-center gap-1"
            >
              <Users className="h-4 w-4" />
              Công lao động
            </Button>
            
            <Button
              variant={activeMetric === 'weight' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveMetric('weight')}
              className="flex items-center gap-1"
            >
              <Activity className="h-4 w-4" />
              Kg Công suất
            </Button>
            
            <Button
              variant={activeMetric === 'productivity' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveMetric('productivity')}
              className="flex items-center gap-1"
            >
              <Zap className="h-4 w-4" />
              Năng suất
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-80 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : chartData.length > 0 ? (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(date) => {
                    const d = new Date(date);
                    return `${d.getDate()}/${d.getMonth() + 1}`;
                  }}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => formatNumberDisplay(value, 0)}
                />
                <Tooltip 
                  formatter={(value: any, name: string) => [
                    `${formatNumberDisplay(value, 2)} ${getUnit()}`, 
                    processNames[name as keyof typeof processNames] || name
                  ]}
                  labelFormatter={(date) => {
                    const d = new Date(date);
                    return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
                  }}
                />
                <Legend
                  formatter={(value) => processNames[value as keyof typeof processNames] || value}
                  wrapperStyle={{
                    paddingTop: '16px',
                    fontSize: '14px'
                  }}
                />
                
                {/* Dynamic Lines based on processes */}
                {processes.map((processKey) => (
                  <Line
                    key={processKey}
                    type="monotone"
                    dataKey={processKey}
                    stroke={processColors[processKey as keyof typeof processColors]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                    connectNulls={false}
                  />
                ))}
                
                {/* Dynamic Average Reference Lines */}
                {processes.map((processKey) => (
                  <ReferenceLine
                    key={`avg-${processKey}`}
                    y={averageValues[processKey]}
                    stroke={processColors[processKey as keyof typeof processColors]}
                    strokeDasharray="8 8"
                    strokeWidth={1}
                    opacity={0.7}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-80 flex items-center justify-center">
            <p className="text-gray-500">Không có dữ liệu biểu đồ</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MetalCharts;